/**
 * Implementation extended from:
 * https://www.contentful.com/developers/docs/tutorials/general/getting-started-with-rich-text-field-type/
 * https://github.com/contentful/rich-text/tree/master/packages/rich-text-react-renderer
 */

/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import type { Options } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import type { Block, Document, Text } from '@contentful/rich-text-types';

import { Link, breakpoints, typography2 } from '@coursera/cds-core';
import type { ButtonName, SectionName } from '@coursera/event-pulse-types';

import { isExternalLink } from 'bundles/common/utils/urlUtils';
import type { DdpFieldsAboveFoldFragment } from 'bundles/degree-description/__generated__/contentfulTypes';
import IconLabel from 'bundles/degree-description/components/shared/IconLabel';
import { useDegreeDescriptionPageTheme } from 'bundles/degree-description/degreeDescriptionPageTheme';
import type { DegreeDescriptionPageTheme } from 'bundles/degree-description/degreeDescriptionPageTheme';
import { useDegreeDescriptionEventingContext } from 'bundles/degree-description/providers/DegreeDescriptionEventingProvider';
import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';

type AboutFields =
  | 'overviewAbout'
  | 'admissionsAbout'
  | 'academicsAbout'
  | 'careersAbout'
  | 'studentExperienceAbout'
  | 'tuitionAbout';

// This generic type grabs the nested 'links' property from all the above AboutFields RichText types
// Gotta explicitly define the fields above because there's no auto-generated, generic 'RichText' type
// from Contentful
type AboutLinksGeneric<T extends AboutFields> = NonNullable<DdpFieldsAboveFoldFragment[T]>['links'];

// This sets a 'discriminated union' of all the About field types, allowing the AboutLinksType to be
// any of the above 'links' from those RichText fields
type AboutLinksType = { [K in AboutFields]: AboutLinksGeneric<K> }[AboutFields];

type ButtonClickHandler = (name: ButtonName, linkURL?: string | null, linkName?: string | null) => () => void;

export type RichTextProps = {
  content: Document;
  contentLinks?: AboutLinksType;
  descriptionColor?: string;
  invertLink?: boolean;
  linkTrackingName?: string;
  buttonTrackingNameV3?: ButtonName;
  trackingSection?: SectionName;
  trackingIndex?: number;
  buttonClickHandler?: ButtonClickHandler;
  richTextRenderOptions?: Options;
};

const styles = {
  description: (theme: DegreeDescriptionPageTheme, descriptionColor?: string) => css`
    h3 {
      ${theme.typography.h1semibold};
      color: ${descriptionColor || 'var(--cds-color-neutral-primary)'};
      ${theme.mobileFontSizeStyle?.header};
    }

    h4 {
      ${theme.typography.h2semibold}
      color: ${descriptionColor || 'var(--cds-color-neutral-primary)'};
      padding-top: 16px;
      ${theme.mobileFontSizeStyle?.subHeader};
    }

    p {
      ${typography2.bodyPrimary};
      color: ${descriptionColor || 'var(--cds-color-neutral-primary)'};
      padding-top: 16px;
      ${theme.mobileFontSizeStyle?.body};
    }

    li > p {
      margin: 0;
      padding: 0;
    }
  `,
  iconList: (theme: DegreeDescriptionPageTheme) => css`
    width: calc(50% - 32px);
    ${breakpoints.down('sm')} {
      width: 100%;
    }

    display: inline-block;
    margin-right: var(--cds-spacing-400);
    padding-top: var(--cds-spacing-300);

    p {
      padding-top: 0;
    }

    .rc-IconList .description-container {
      ${theme.mobileFontSizeStyle?.body};
    }
  `,
};

// Util function to prevent from rendering empty tag, copied from below github issue
// https://github.com/contentful/rich-text/issues/101
export const isEmptyChild = (children: React.ReactNode) => {
  return children?.toString().trim() === '';
};

export const getDefaultRichTextOptions = (
  contentLinks?: AboutLinksType,
  invertLink?: boolean,
  linkTrackingName?: string,
  buttonTrackingNameV3?: ButtonName,
  buttonClickHandler?: ButtonClickHandler
): Options => {
  return {
    renderNode: {
      [BLOCKS.HEADING_3]: (_, children) => {
        if (isEmptyChild(children)) return null;
        return <h3>{children}</h3>;
      },

      [BLOCKS.HEADING_4]: (_, children) => {
        if (isEmptyChild(children)) return null;
        return <h4>{children}</h4>;
      },

      [BLOCKS.PARAGRAPH]: (_, children) => {
        if (isEmptyChild(children)) return null;
        return <p>{children}</p>;
      },

      [INLINES.HYPERLINK]: (node) => {
        const textBlock = node?.content?.[0] as Text;

        return (
          <Link
            {...(linkTrackingName && { component: TrackedLink2 })}
            {...(linkTrackingName && { trackingName: linkTrackingName })}
            {...(buttonTrackingNameV3 &&
              buttonClickHandler && {
                onClick: buttonClickHandler(buttonTrackingNameV3, node.data.uri, textBlock?.value),
              })}
            variant="quiet"
            typographyVariant="subtitleMedium"
            href={node.data.uri}
            target={isExternalLink(node.data.uri) ? '_blank' : undefined}
            rel={isExternalLink(node.data.uri) ? 'noopener noreferrer' : undefined}
            invert={invertLink}
          >
            {textBlock?.value}
          </Link>
        );
      },

      [BLOCKS.EMBEDDED_ENTRY]: (node) => {
        const theme = useDegreeDescriptionPageTheme();
        const blockNode = node as Block;
        const id = blockNode?.data?.target?.sys?.id;
        const entry = contentLinks?.entries?.block?.find((link) => link?.sys?.id === id);

        switch (entry?.__typename) {
          case 'GeneralIconLabel':
            return (
              <IconLabel
                iconLabel={entry.iconLabel}
                iconVariant={entry.iconVariant}
                description={entry.description}
                containerStyle={styles.iconList(theme)}
              />
            );

          default:
            return null;
        }
      },
    },
  };
};

export const filterRichTextContentByNodeType = (content: Document, nodeTypes: string[]) => {
  const filteredContent = { ...content };
  filteredContent.content = filteredContent.content.filter((node) => nodeTypes.includes(node.nodeType));
  return filteredContent;
};

// export rich text render function
export const renderRichText = ({
  content,
  contentLinks,
  invertLink,
  linkTrackingName,
  buttonTrackingNameV3,
  buttonClickHandler,
  richTextRenderOptions,
}: RichTextProps): React.ReactNode => {
  const { renderNode, renderMark, renderText } = getDefaultRichTextOptions(
    contentLinks,
    invertLink,
    linkTrackingName,
    buttonTrackingNameV3,
    buttonClickHandler
  );
  return documentToReactComponents(content, {
    renderNode: { ...renderNode, ...richTextRenderOptions?.renderNode },
    renderMark: { ...renderMark, ...richTextRenderOptions?.renderMark },
    renderText: richTextRenderOptions?.renderText || renderText,
  });
};

const RichText = ({
  content,
  contentLinks,
  descriptionColor,
  invertLink,
  linkTrackingName,
  buttonTrackingNameV3,
  trackingSection,
  trackingIndex,
  richTextRenderOptions,
}: RichTextProps) => {
  const theme = useDegreeDescriptionPageTheme();
  const { trackClickButton } = useDegreeDescriptionEventingContext();

  const buttonClickHandler: ButtonClickHandler = (name, linkURL, linkName) => () => {
    if (trackingSection) {
      trackClickButton(name, trackingSection, linkURL, linkName, trackingIndex);
    }
  };

  return (
    <div className="rc-RichText" css={styles.description(theme, descriptionColor)}>
      {renderRichText({
        content,
        contentLinks,
        descriptionColor,
        invertLink,
        linkTrackingName,
        buttonTrackingNameV3,
        buttonClickHandler,
        richTextRenderOptions,
      })}
    </div>
  );
};

export default RichText;
